import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChildren,
} from '@angular/core';
import { BehaviorSubject, combineLatest, firstValueFrom, merge, ReplaySubject, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  skip,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  CustomFormData,
  FormFields,
  OrderformTrackingCollectorService,
  OrderFormValidationResult,
} from '@orderform/widgets';
import {
  AbstractControl,
  FormControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { OrderformFascade } from '../../fascades/orderform.fascade';
import { formMarkAllAsDirty } from '@digistore/ds24-ui/rxjs/formMarkAllAs';
import {
  InterfaceField,
  InterfaceFields,
  FIELD_LIST_WITH_UPDATE_TRIGGER,
  FIELD_LIST_WITH_ON_BLUR_CHANGE,
} from './inputs-generator.model';
import { isFormFieldVisible, mapFieldListToFormStructure } from './map-field-list-to-form-structure';
import { GeneratorInputComponent } from './generator-input/generator-input.component';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { isPageBuilderElementVisible } from '../../misc/pagebuilder';
import { getInvalidFieldListFromFormGroup } from '../../misc/get-invalid-field-list-from-form-group';
import { isEqual } from 'lodash-es';
import { OrderFormFieldBlurPayload, OrderFormFieldFocusPayload } from 'libs/tracking-core/src/lib/types/events/OrderForm';

export const INFO_TEXT_WIDGET_DISPLAY = $localize`:@@global.info.widget_display:Diese Komponente wird nur angezeigt,
wenn die erforderlichen Optionen in Ihren Produkteinstellungen aktiviert sind.`;

export const INFO_TEXT_WIDGET_DISPLAY_ON_ADDITIONAL_FIELDS = $localize`:@@global.info.widget_display_on_additional_fields:Diese Komponente wird nur angezeigt,
  wenn für das ausgewählte Produkt zusätzliche Eingabefelder hinterlegt sind.`;

@Component({
  selector: 'orderform-inputs-generator',
  templateUrl: './inputs-generator.component.html',
  styleUrls: ['./inputs-generator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputsGeneratorComponent implements OnInit, OnDestroy {
  // NOTE: Extended Latin validation character whitelist.
  // Includes:
  //  * Basic Latin letters A-Z a-z
  //  * Latin-1 Supplement \u00C0-\u00FF
  //  * Latin Extended-A & B \u0100-\u024F
  //  * Latin Extended Additional \u1E00-\u1EFF
  //  * Digits 0-9, whitespace (\s) and selected punctuation characters
  private static readonly EXTENDED_LATIN_REGEXP = /^[A-Za-z\u00C0-\u024F\u1E00-\u1EFF0-9\s'.,/#!$%^&*;:{}=_`~()\-]+$/;

  @ViewChildren(GeneratorInputComponent)
  generatorInputs: GeneratorInputComponent[];
  fields$ = new BehaviorSubject<FormFields.Field[]>([]);
  #tracking = inject(OrderformTrackingCollectorService);
  data$ = new BehaviorSubject<CustomFormData>({});

  public static uniqueIdGenerator = 0;
  public uniqueComponentId = InputsGeneratorComponent.uniqueIdGenerator++;

  private _formGroup = new UntypedFormGroup({});
  #syncBackendValues = new Subject<CustomFormData>();

  private _saveFormGroupValueChange = merge(this._formGroup.valueChanges, this.#syncBackendValues.pipe(skip(1)))
    .pipe(
      filter(() => !this._isAutomaticValueChangeActive),
      distinctUntilChanged((a, b) => isEqual(a, b)),
      filter(() => isPageBuilderElementVisible(this._element.nativeElement))
    )
    .subscribe((value) => {
      this.dataChanges.emit(value);
    });
  private _destroy$ = new ReplaySubject<void>(1);
  private _isAutomaticValueChangeActive = false;
  private _focusField: string = null;

  @Input() disableDataFromBackend = false;

  @Input() set fields(fields: FormFields.Field[]) {
    const modFields: FormFields.Field[] = [];

    fields.forEach((field) => {
      /**
       * Wir haben im BE unter den Custom Forms ein Dropdown welches mehrere Auswahlmöglichkeiten haben kann.
       * Diese Auswahl des Drodowns sollen als Checkbox angezeigt werden. Aufgrund, dass wir aus dem BE nur ein Element erhalten
       * aber an das BE mehrere Daten seperat schicken müssen, generieren wir der einfacherhalb einige Hidden Elements die den Checkboxen zugeordnet werden
       * Grund: KISS
       */
      if (this._fieldIsCheckboxList(field)) {
        (field.options as FormFields.SelectOption[]).forEach((option) => {
          const [key] = Object.keys(option);
          const [value] = Object.values(option);
          const optionPostname = field.postname + '_' + key;
          modFields.push({
            type: 'hidden',
            postname: optionPostname,
            id: field.id + '_' + key,
            label: value,
          });
        });
      }
      modFields.push(field);
    });

    this.fields$.next(modFields);
  }

  @Input() set data(data: CustomFormData) {
    this.data$.next(data || {});
  }

  @Output() readonly dataChanges = new EventEmitter<CustomFormData>();
  @Output() readonly stateChange = this._formGroup.statusChanges.pipe(distinctUntilChanged());
  private _excludeFormOrderValidation = false;
  @Input() set excludeFromOrderValidation(exclude: any) {
    this._excludeFormOrderValidation = coerceBooleanProperty(exclude);
  }

  #backendValidationErrorByField$ = this._orderFormFascade.validation$.pipe(
    map((data) => data.errors_by_postname),
    distinctUntilChanged()
  );

  /**
   * Attention!
   * This form will not regenerate of this.data$ will changed! see _syncData()
   */
  formGroup$ = combineLatest([
    this.fields$.pipe(distinctUntilChanged((a, b) => isEqual(a, b))),
    this._orderFormFascade.requestPending$.pipe(
      // emit value only if data had comes from BE
      map((onUpdated) => {
        return this.data$.value;
      }),
      distinctUntilChanged((a, b) => isEqual(a, b))
    ),
  ]).pipe(
    withLatestFrom(this.#backendValidationErrorByField$),
    tap((x) => {
      this._isAutomaticValueChangeActive = true;
    }),
    map(([[fields, preFilledCustomForm], backendValidationError]) => {
      const formGroup = this._formGroup;
      // find controls to remove
      Object.keys(formGroup.controls)
        .filter((controlName) => {
          return !fields.find((field) => field.postname === controlName);
        })
        .forEach((controlName) => {
          if (formGroup.contains(controlName)) {
            formGroup.removeControl(controlName);
          }
        });

      /**
       * Foreach each field of custom form
       */
      fields
        /**
         * Filter only input for formControls
         */
        .filter((field) => ['headline'].indexOf(field.type) === -1)
        .forEach((field) => {
          const lastField = formGroup.get(field.postname);
          const lastFieldStatus = {
            dirty: lastField ? lastField.dirty : false,
            touched: lastField ? lastField.touched : false,
            invalid: lastField ? lastField.invalid : false,
          };

          const inputValue = this._getInputValue(field, preFilledCustomForm);
          /**
           * Create / Update Control and set value
           */
          if (!formGroup.get(field.postname)) {
            const createdControl = new UntypedFormControl(
              {
                value: inputValue,
                /**
                 * Do not disable control because disabled controls do not emit values
                 */
                disabled: false,
              },
              {
                updateOn: FIELD_LIST_WITH_ON_BLUR_CHANGE.includes(field.postname) ? 'blur' : 'change',
              }
            );
            if (FIELD_LIST_WITH_UPDATE_TRIGGER.includes(field.postname)) {
              createdControl.valueChanges
                .pipe(
                  distinctUntilChanged(),
                  debounceTime(10),
                  withLatestFrom(this._orderFormFascade.pending$, this._orderFormFascade.submitted$),
                  takeUntil(this._destroy$)
                )
                .subscribe(async ([value, pending, submitted]) => {
                  if (!pending || !submitted) {
                    firstValueFrom(this._orderFormFascade.update$());
                  }
                });
            }
            formGroup.setControl(field.postname, createdControl);
          } else if (this._focusField !== field.postname) {
            const tmpControl = formGroup.get(field.postname) as FormControl;
            tmpControl.setValue(tmpControl.untouched ? field.is_checked ?? field.value : inputValue, {
              emitEvent: false,
            });
          }
          const control = formGroup.get(field.postname);

          /**
           * Set validations
           */
          const validation: ValidatorFn[] = this._getFieldValidatorList(control, field, backendValidationError);
          control.setValidators(validation);
          control.setAsyncValidators(field.asyncValidators);

          /**
           * Status vom letzten FormControl übertragen.
           */
          // check last stats
          if (lastFieldStatus.touched) {
            control.markAsTouched();
          }
          if (!!backendValidationError[field.postname]) {
            control.markAsDirty();
          }

          if (backendValidationError[field.postname]) {
            //control.markAsPristine();
            control.updateValueAndValidity();
          }

          if (lastFieldStatus.invalid) {
            control.updateValueAndValidity();
          }
        });

      // set all inputs to touched =>  Displays errors instantly
      if (this._orderFormFascade.submitted$.value) {
        formGroup.markAllAsTouched();
      }

      return formGroup;
    }),
    tap((formGroup) => {
      this._isAutomaticValueChangeActive = false;
      setTimeout(() => {
        this.#syncBackendValues.next(this._formGroup.value);
      }, 0);
    }),
    shareReplay(1) // IMPORTANT for performance e.g dependsOn
  );

  private _getInputValue(field: FormFields.Field, preFilledCustomForm: CustomFormData) {
    if (field.type === 'checkbox') {
      // set value from BE first, if exists
      return field.is_readonly
        ? field.is_checked //DS-11366!!
        : preFilledCustomForm[field.postname] ?? field.is_checked ?? field.value;
    } else {
      const beCalculatedValue = !preFilledCustomForm[field.postname]
        ? field.value
        : preFilledCustomForm[field.postname];

      const generatorInputEl: GeneratorInputComponent = this.generatorInputs?.find(
        (e) => e.field.postname === field.postname
      );

      /**
       * Wenn das Feld focusiert ist oder das BE einen '' als Value zurück gibt,
       * soll immer der Wert des Users genommen werden (falls vorhanden)
       */
      if (generatorInputEl && (generatorInputEl.focus$.value || beCalculatedValue === '')) {
        return generatorInputEl.getFormControl().value;
      }

      return beCalculatedValue;
    }
  }

  fromGroupValue$ = this.formGroup$.pipe(
    switchMap((formGroup) => formGroup.valueChanges.pipe(startWith(formGroup.value))),
    shareReplay(1)
  );

  // new structure only for template
  fieldStructure$ = this.fields$.pipe(map((fields) => mapFieldListToFormStructure(fields, this.fromGroupValue$)));

  fieldValidations$ = this._orderFormFascade.validation$.pipe(
    map((validations) => {
      return validations.errors_by_postname;
    }),
    shareReplay(1)
  );

  successFieldValidations$ = this._orderFormFascade.validation$.pipe(
    map((validations) => {
      return validations.success_by_postname;
    }),
    shareReplay(1)
  );

  vm$ = combineLatest([this.formGroup$, this.successFieldValidations$, this.fieldStructure$]).pipe(
    map(([formGroup, successMessages, fieldsStructure]) => {
      return {
        formGroup,
        successMessages,
        fieldsStructure,
      };
    })
  );

  dateFormat = null;
  hourFormat: 12 | 24 = null;

  trackByFieldSettings = (index, item: InterfaceField) => {
    // important to check all settings configurations
    return item.field.postname;
  };

  trackByStructure = (index, item: InterfaceFields) => {
    return item.fields.map((f) => f.field.postname).join(',');
  };

  private _subscriptionFormValidation = this._orderFormFascade.formValidation.subscribe(() => {
    // Check if the id is #countryAndZipcodeInput and do nothing if so
    // Special case for the country and zipcode input dialog on express checkout
    if (this._element.nativeElement.id === 'countryAndZipcodeInput') {
      return Promise.resolve({
        valid: true,
        component: 'countryAndZipcodeInput',
        invalidFieldList: [],
      });
    }
    const component = this._element.nativeElement.closest('[ng-version]').tagName;
    if (this._excludeFormOrderValidation || !isPageBuilderElementVisible(this._element.nativeElement)) {
      return Promise.resolve({
        valid: true,
        component,
        invalidFieldList: [],
      });
    }
    return this._onFormValidation();
  }, this._element);

  private _subscriptionBackendValidation = this.#backendValidationErrorByField$
    .pipe(withLatestFrom(this.fields$), skip(1))
    .subscribe(([backendValidationError, fieldList]) => {
      fieldList
        .filter((field) => this._formGroup.get(field.postname))
        .forEach((field) => {
          const control = this._formGroup.get(field.postname);
          const validation: ValidatorFn[] = this._getFieldValidatorList(control, field, backendValidationError);
          control.setValidators(validation);
          control.setAsyncValidators(field.asyncValidators);

          // Ensure validation is updated correctly
          control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        });
    });

  constructor(
    private _orderFormFascade: OrderformFascade,
    private _cdr: ChangeDetectorRef,
    private _element: ElementRef<HTMLElement>
  ) {}

  private _setupFormValidation(
    field: FormFields.Field,
    control: AbstractControl,
    backendValidationError: { [key: string]: string }
  ) {}

  ngOnInit(): void {
    /**
     * We need cdr there because we seperated the inputs in another component...
     * (Every input change would trigger the component cdr now we need do this manually)
     */
    this.fromGroupValue$.pipe(debounceTime(250), takeUntil(this._destroy$)).subscribe(() => {
      this._cdr.detectChanges();
    });

    this._syncData();
  }

  /**
   * we need also subscribe on data$ because the same widget can exist in mobile and desktop view
   * at the end, both widgets are rendered! We need to make sure, that both forms has same filled values
   * else the validation do not work! https://digistore.atlassian.net/browse/DS-10709
   */
  /**
   * @TODO: muss überarbeitet werden, es darf nur einen sync bei einer Manuellen-Eingabe geben!!!
   * @private
   */
  private _syncData() {
    this.data$
      .pipe(
        distinctUntilChanged((a, b) => isEqual(a, b)),
        filter(() => !isPageBuilderElementVisible(this._element.nativeElement)),
        debounceTime(100),
        takeUntil(this._destroy$)
      )
      .subscribe((data) => {
        this.fields$.value.forEach((field) => {
          const formControl = this._formGroup.get(field.postname);
          const inputValue = this._getInputValue(field, data);
          formControl.patchValue(inputValue);
        });
        // If data transfers via input, trigger change detection
        this._cdr.detectChanges();
      });
  }

  /**
   * Gibt die Validators für ein Form-Field zurück.
   * @param field
   * @param backendValidationError
   * @private
   */
  private _getFieldValidatorList(
    control: AbstractControl,
    field: FormFields.Field,
    backendValidationError: {
      [key: string]: string;
    }
  ) {
    const validation: ValidatorFn[] = [];
    const required = field.is_required;

    if (required) {
      validation.push((tmpControl: AbstractControl) => {
        if (isFormFieldVisible(tmpControl.parent.value, field.depends_on)) {
          return Validators.required(tmpControl);
        }
        return null;
      });

      if (field.type === 'checkbox') {
        validation.push((validationControl) => {
          if (isFormFieldVisible(validationControl.parent.value, field.depends_on)) {
            return typeof validationControl.value === 'boolean' && validationControl.value
              ? null
              : { checkboxRequired: true };
          }
          return null;
        });
      }
    }

    if (field.is_latin_only) {
      validation.push((validationControl) => {
        if (isFormFieldVisible(validationControl.parent.value, field.depends_on)) {
          /**
           * https://gist.github.com/germanattanasio/84cd25395688b7935182
           */
          const value = validationControl.value;
          const regExp = InputsGeneratorComponent.EXTENDED_LATIN_REGEXP;
          const ableToReplaceAllChars = value.replace(regExp, '');

          /**
           * validate only if value exists
           * * value would exist, if field ist required
           */
          if (value) {
            /**
             * If able to replace all allowed Chars -> allow string
             */
            return ableToReplaceAllChars?.trim() === '' ? null : { latinOnly: true };
          }
        }
        return null;
      });
    }

    if (field.regex) {
      validation.push((validationControl) => {
        if (isFormFieldVisible(validationControl.parent.value, field.depends_on)) {
          const value = validationControl.value;
          const regExp = field.regex;

          /**
           * validate only if value exists
           * * value would exist, if field ist required
           */
          if (value) {
            return value.match(regExp) ? null : { pattern: true };
          }
        }
        return null;
      });
    }

    if (field.validators) {
      validation.push(...field.validators);
    }

    if (backendValidationError[field.postname]) {
      validation.push((validationControl) => {
        return { custom: backendValidationError[field.postname] };
      });

      control.valueChanges
        .pipe(
          distinctUntilChanged(),
          filter(() => !this._isAutomaticValueChangeActive),
          take(1),
          debounceTime(10)
        )
        .subscribe((v) => {
          this._orderFormFascade.removeValidationError(field.postname);
        });
    }
    return validation;
  }

  private _fieldIsCheckboxList(field: FormFields.Field): boolean {
    return field.type === 'select' && field.multi_select && !!field.options;
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
    this._subscriptionFormValidation.unsubscribe();

    this._saveFormGroupValueChange.unsubscribe();
    this._subscriptionBackendValidation.unsubscribe();
  }

  private async _onFormValidation(): Promise<OrderFormValidationResult> {
    const backendValidationError = await firstValueFrom(this.#backendValidationErrorByField$);
    Object.keys(backendValidationError).forEach((field) => {
      this._orderFormFascade.removeValidationError(field);
    });

    const component = this._element.nativeElement.closest('[ng-version]').tagName;
    const isExpressCheckoutClicked = await firstValueFrom(this._orderFormFascade.isExpressCheckoutClicked$);
    if (isExpressCheckoutClicked && component !== 'DS-ORDERFORM-INPUT-CHECKBOXES') {
      return Promise.resolve({
        valid: true,
        component,
        invalidFieldList: [],
      });
    }

    formMarkAllAsDirty(this._formGroup);
    if (this._formGroup.pending) {
      await firstValueFrom(this._formGroup.statusChanges.pipe(filter((status) => status !== 'PENDING')));
    }
    /**
     * We exclude here backend errors from form validation,
     * because backend take care of valid input, we only display the error.
     */

    let invalidFieldList = getInvalidFieldListFromFormGroup(this._formGroup);
    if (isExpressCheckoutClicked) {
      invalidFieldList = invalidFieldList.filter((item) => item.id.includes('refund_waiver')); // refund checkbox identifier
    }
    /*
      .filter(item => {
        if (backendValidationError[item.id] && item.errors.filter(error => !error.startsWith('custom:')).length === 0) {
          return false;
        }
        return true;
      });
*/
    return Promise.resolve({
      valid: this._formGroup.valid, // invalidFieldList.length === 0,
      component,
      invalidFieldList,
    });
  }

  public submit(): boolean {
    formMarkAllAsDirty(this._formGroup);
    return this._formGroup.valid;
  }

  public isValid(): boolean {
    return this._formGroup.valid;
  }

  public isTouched(): boolean {
    return this._formGroup.touched;
  }

  public isFieldValid(field: string): boolean {
    return !!this._formGroup.get(field)?.valid;
  }

  public setFocusField(postname: string) {
    this._focusField = postname;
  }

  public removeFocusField(postname: string) {
    if (this._focusField === postname) {
      this._focusField = null;
    }
  }

  onFieldFocused(event: OrderFormFieldFocusPayload) {
    const field = this.fields$.value.find((f) => f.postname === event.fieldName);
    if (!field) return;
    this._trackEvent('OrderFormFieldFocus', event);
  }

  onFieldBlurred(event: OrderFormFieldBlurPayload) {
    const field = this.fields$.value.find((f) => f.postname === event.fieldName);
    if (!field) return;
    this._trackEvent('OrderFormFieldBlur', event);
  }

  private _trackEvent(
    eventType: 'OrderFormFieldFocus' | 'OrderFormFieldBlur',
    event: OrderFormFieldBlurPayload | OrderFormFieldFocusPayload
  ) {
    if (!event) return;
    this.#tracking.track(eventType, event);
  }
}
